// client/constants/passkeys.js
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import api from "./api";

/* ───────────── Utils ───────────── */

const FALLBACK_ERR = "Unbekannter Fehler";

const REG_MUTEX_KEY = "wrtk:passkey:registering";

function unwrapErr(e, fallback = FALLBACK_ERR) {
  return (
    e?.response?.data?.msg ||
    e?.response?.data?.error ||
    e?.error?.message ||
    e?.message ||
    fallback
  );
}

// ───────── Lokales Geräte-Flag für Passkeys ─────────
function makeLocalDeviceKey() {
  try {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const uid = user?.id ? String(user.id) : "anon";
    const ua = typeof navigator !== "undefined" && navigator.userAgent ? navigator.userAgent : "na";
    // nur ein kurzer, stabiler Teil des UA – kein Fingerprinting, nur pro-Gerät pro-User
    const tag = ua.slice(0, 32);
    return `wrtk.passkey.device.v1:${uid}:${tag}`;
  } catch {
    return `wrtk.passkey.device.v1:anon:na`;
  }
}

export function markLocalPasskeyInstalled() {
  try {
    localStorage.setItem(makeLocalDeviceKey(), "1");
  } catch { /* ignore */ }
}

export function hasLocalPasskeyInstalled() {
  try {
    return localStorage.getItem(makeLocalDeviceKey()) === "1";
  } catch {
    return false;
  }
}

function mapWebAuthnError(e, fallback = FALLBACK_ERR) {
  // Häufige Browser-/WebAuthn-Fehler klarer benennen
  const name = e?.name || "";
  const msg = String(e?.message || "");
  if (name === "NotAllowedError" || /notallowed/i.test(name) || /request.*aborted/i.test(msg)) {
    return "Vorgang abgebrochen.";
  }
  if (name === "InvalidStateError") {
    return "Ungültiger Zustand – ggf. existiert bereits ein Passkey für diesen Account.";
  }
  if (name === "SecurityError") {
    return "Sicherheitsfehler – bitte HTTPS verwenden oder Seite neu laden.";
  }
  if (name === "AbortError" || /aborted|abort/i.test(msg)) {
   return "Vorgang abgebrochen.";
 }
 // Safari/Edge-Formulierungen
 if (/operation.*(timed.*out|was.*not.*allowed)/i.test(msg)) {
   return "Vorgang abgebrochen oder Zeitüberschreitung.";
 }
 if (name === "UnknownError") {
   return "Unerwarteter Fehler im Authenticator/Browser.";
 }
  if (/invalid|malformed|challenge/i.test(msg)) {
    return "Challenge ungültig – Seite neu laden und erneut versuchen.";
  }
  return fallback;
}

/**
 * Manche Backends verschachteln die Options:
 * - { options: {...} }
 * - { publicKey: {...} }
 * - { options: { publicKey: {...} } }
 * Diese Funktionen geben immer das Plain-Objekt (publicKey optionsJSON) zurück.
 */
function normalizeCreationOptions(obj) {
  let o = obj;
  if (o && o.options && typeof o.options === "object") o = o.options;
  if (o && o.publicKey && typeof o.publicKey === "object") o = o.publicKey;

  const ok =
    o &&
    typeof o === "object" &&
    o.challenge &&
    o.rp &&
    o.user &&
    Array.isArray(o.pubKeyCredParams);
 if (ok) {
   if (!("authenticatorSelection" in o)) {
     console.warn("[Passkey] Hinweis: authenticatorSelection fehlt (serverseitig).");
   }
   if (!("attestation" in o)) {
     console.warn("[Passkey] Hinweis: attestation fehlt (serverseitig, meist 'none').");
   }
 }

  if (!ok) {
    console.error("[Passkey] Invalid creation options shape:", {
      hasChallenge: !!o?.challenge,
      hasRp: !!o?.rp,
      hasUser: !!o?.user,
      hasParams: Array.isArray(o?.pubKeyCredParams),
      keys: o ? Object.keys(o) : "none",
      raw: obj,
    });
    throw new Error("Ungültige Passkey-Options (challenge/rp/user fehlen)");
  }
  return o;
}

function normalizeRequestOptions(obj) {
  let o = obj;
  if (o && o.options && typeof o.options === "object") o = o.options;
  if (o && o.publicKey && typeof o.publicKey === "object") o = o.publicKey;

  const ok = o && typeof o === "object" && o.challenge;
  if (!ok) {
    console.error("[Passkey] Invalid request options shape:", {
      hasChallenge: !!o?.challenge,
      keys: o ? Object.keys(o) : "none",
      raw: obj,
    });
    throw new Error("Ungültige Login-Options (challenge fehlt)");
  }
  return o;
}

/* ───────────── Feature-Checks ───────────── */

export async function isPasskeyAvailable() {
  try {
    if (typeof window === "undefined") return false;
    if (!window.isSecureContext) return false; // HTTPS/localhost
    if (!("PublicKeyCredential" in window)) return false;

    if (PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
      const uvpaa = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!uvpaa) return false;
    }
    // Optional check; kein Hardfail
    if (PublicKeyCredential.isConditionalMediationAvailable) {
      try {
        await PublicKeyCredential.isConditionalMediationAvailable();
      } catch { /* ignore */ }
    }
    return true;
  } catch {
    return false;
  }
}

/** Zusätzlicher Check, ob Conditional UI (Autofill) verfügbar sein könnte */
export async function isConditionalUiLikelyAvailable() {
  try {
    if (typeof window === "undefined" || !window.isSecureContext) return false;
    if (!("PublicKeyCredential" in window)) return false;
    if (!PublicKeyCredential.isConditionalMediationAvailable) return false;
    const ok = await PublicKeyCredential.isConditionalMediationAvailable();
    return !!ok;
  } catch { return false; }
}

/* ───────────── Login (2FA oder passwortlos) ───────────── */

/**
 * Login mit Passkey
 * @param {string} [emailHint]
 * @param {{ signal?: AbortSignal, silent?: boolean }} [opts]
 */
export async function loginWithPasskey(emailHint, opts = {}) {
  const { signal, silent = false, timeoutMs } = opts;
  if (sessionStorage.getItem(REG_MUTEX_KEY)) throw new Error("Registrierung läuft – bitte zuerst abschließen.");
  try {
    const body = emailHint ? { email: String(emailHint).trim().toLowerCase() } : {};
    const { data } = await api.post("/webauthn/login/start", body, { signal, silent });
    if (!data?.options) throw new Error(data?.msg || "Keine WebAuthn-Optionen");

    const options = normalizeRequestOptions(data.options);

    // simplewebauthn v10: optionsJSON übergeben
    const assertion = await startAuthentication({ optionsJSON: options });
    if (!assertion?.id) throw new Error("WebAuthn-Login abgebrochen");

    const fin = await api.post("/webauthn/login/finish", assertion, { signal, silent });
    if (!fin.data?.erfolg) throw new Error(fin.data?.msg || "Login finish fehlgeschlagen");
    return fin.data; // { erfolg: true, ... }
  } catch (e) {
    throw new Error(mapWebAuthnError(e, unwrapErr(e, "Login mit Passkey fehlgeschlagen")));
  }
}

/**
 * Login via Conditional UI (Autofill) – zeigt System-Autofill, wenn verfügbar.
 * @param {string} [emailHint]
 * @param {{ signal?: AbortSignal, silent?: boolean }} [opts]
 */
export async function loginWithPasskeyConditional(emailHint, opts = {}) {
  const { signal, silent = false, timeoutMs } = opts;
  if (sessionStorage.getItem(REG_MUTEX_KEY)) throw new Error("Registrierung läuft – bitte zuerst abschließen.");
  try {
   if (!(await isConditionalUiLikelyAvailable())) {
     return await loginWithPasskey(emailHint, opts);
   }
    const body = emailHint ? { email: String(emailHint).trim().toLowerCase() } : {};
   const ctrl = typeof AbortSignal !== "undefined" && AbortSignal.any
     ? new AbortController()
     : null;
   const combinedSignal = ctrl
     ? AbortSignal.any([signal].filter(Boolean).concat(
         timeoutMs ? AbortSignal.timeout(timeoutMs) : []
       ))
     : signal;
  const { data } = await api.post("/webauthn/login/start", body, { signal: combinedSignal, silent });

    if (!data?.options) throw new Error(data?.msg || "Keine WebAuthn-Optionen");
    const options = normalizeRequestOptions(data.options);

    // Wichtig: mediation NICHT in optionsJSON mergen, sondern als Option übergeben
    const assertion = await startAuthentication({
      optionsJSON: options,
      mediation: "conditional",
    });
    if (!assertion?.id) throw new Error("WebAuthn-Login abgebrochen");

    const fin = await api.post("/webauthn/login/finish", assertion, { signal: combinedSignal, silent });
    if (!fin.data?.erfolg) throw new Error(fin.data?.msg || "Login finish fehlgeschlagen");
    return fin.data;
  } catch (e) {
    throw new Error(mapWebAuthnError(e, unwrapErr(e, "Conditional Passkey-Login fehlgeschlagen")));
  }
}

/* ───────────── Registrierung (FaceID/TouchID/Hello) ───────────── */

/**
 * Passkey registrieren
 * @param {{ autoLogin?: boolean, signal?: AbortSignal, silent?: boolean }} [opts]
 */
export async function registerPasskey({ autoLogin = false, signal, silent = false } = {}) {
  try {
    if (sessionStorage.getItem(REG_MUTEX_KEY)) throw new Error("Einrichtung läuft bereits.");
    sessionStorage.setItem(REG_MUTEX_KEY, "1");
    // 1) Start
    const { data } = await api.post("/webauthn/register/start", {}, { signal, silent });
    if (!data?.options) throw new Error(data?.msg || "Keine Register-Optionen");

    // 2) Struktur korrigieren und OS-Prompt starten
    const options = normalizeCreationOptions(data.options);
    console.log("[Passkey] starting registration…");

    // simplewebauthn v10: { optionsJSON } übergeben
    const attestation = await startRegistration({ optionsJSON: options });
    if (!attestation?.id) throw new Error("WebAuthn abgebrochen");

    // 3) Abschluss – Credential direkt senden, NICHT eingewickelt
    const fin = await api.post("/webauthn/register/finish", attestation, { signal, silent });
    if (!fin.data?.erfolg) throw new Error(fin.data?.msg || "Register finish fehlgeschlagen");

    // 3b) WICHTIG: Dieses Gerät lokal als "Passkey vorhanden" markieren
    markLocalPasskeyInstalled();

    // 4) Optional: direkt passwortlos einloggen
    if (autoLogin) {
  const email = window?.wrtk?.currentUser?.email; 
  return await loginWithPasskey(email, { signal, silent });
}
    return fin.data; 
      } catch (e) {
          throw new Error(mapWebAuthnError(e, unwrapErr(e, "Passkey/FaceID konnte nicht eingerichtet werden")));
        } finally {
          try { sessionStorage.removeItem(REG_MUTEX_KEY); } catch {}
  }
}
